// Validazione IBAN internazionale lato client.
// Le prime 2 lettere identificano lo stato; ogni stato ha una lunghezza
// totale fissa (es. Italia = IT + 25 caratteri = 27 totali).
// Deve restare allineato al registro IBAN_LENGTHS del backend (server.py).

export const IBAN_SPECS = {
  AD: [24, "Andorra"], AE: [23, "Emirati Arabi Uniti"], AL: [28, "Albania"],
  AT: [20, "Austria"], AZ: [28, "Azerbaigian"], BA: [20, "Bosnia-Erzegovina"],
  BE: [16, "Belgio"], BG: [22, "Bulgaria"], BH: [22, "Bahrein"],
  BI: [27, "Burundi"], BR: [29, "Brasile"], BY: [28, "Bielorussia"],
  CH: [21, "Svizzera"], CR: [22, "Costa Rica"], CY: [28, "Cipro"],
  CZ: [24, "Rep. Ceca"], DE: [22, "Germania"], DJ: [27, "Gibuti"],
  DK: [18, "Danimarca"], DO: [28, "Rep. Dominicana"], EE: [20, "Estonia"],
  EG: [29, "Egitto"], ES: [24, "Spagna"], FI: [18, "Finlandia"],
  FK: [18, "Isole Falkland"], FO: [18, "Isole Faroe"], FR: [27, "Francia"],
  GB: [22, "Regno Unito"], GE: [22, "Georgia"], GI: [23, "Gibilterra"],
  GL: [18, "Groenlandia"], GR: [27, "Grecia"], GT: [28, "Guatemala"],
  HR: [21, "Croazia"], HU: [28, "Ungheria"], IE: [22, "Irlanda"],
  IL: [23, "Israele"], IQ: [23, "Iraq"], IS: [26, "Islanda"],
  IT: [27, "Italia"], JO: [30, "Giordania"], KW: [30, "Kuwait"],
  KZ: [20, "Kazakistan"], LB: [28, "Libano"], LC: [32, "Santa Lucia"],
  LI: [21, "Liechtenstein"], LT: [20, "Lituania"], LU: [20, "Lussemburgo"],
  LV: [21, "Lettonia"], LY: [25, "Libia"], MC: [27, "Monaco"],
  MD: [24, "Moldavia"], ME: [22, "Montenegro"], MK: [19, "Macedonia del Nord"],
  MN: [20, "Mongolia"], MR: [27, "Mauritania"], MT: [31, "Malta"],
  MU: [30, "Mauritius"], NI: [28, "Nicaragua"], NL: [18, "Paesi Bassi"],
  NO: [15, "Norvegia"], OM: [23, "Oman"], PK: [24, "Pakistan"],
  PL: [28, "Polonia"], PS: [29, "Palestina"], PT: [25, "Portogallo"],
  QA: [29, "Qatar"], RO: [24, "Romania"], RS: [22, "Serbia"],
  RU: [33, "Russia"], SA: [24, "Arabia Saudita"], SC: [31, "Seychelles"],
  SD: [18, "Sudan"], SE: [24, "Svezia"], SI: [19, "Slovenia"],
  SK: [24, "Slovacchia"], SM: [27, "San Marino"], SO: [23, "Somalia"],
  ST: [25, "São Tomé e Príncipe"], SV: [28, "El Salvador"],
  TL: [23, "Timor Est"], TN: [24, "Tunisia"], TR: [26, "Turchia"],
  UA: [29, "Ucraina"], VA: [22, "Città del Vaticano"],
  VG: [24, "Isole Vergini Britanniche"], XK: [20, "Kosovo"],
};

/**
 * Valida un IBAN internazionale (spazi ignorati, case-insensitive).
 * @returns {{ normalized: string, valid: boolean, country: string|null,
 *             countryName: string|null, expectedLength: number|null,
 *             message: string }}
 */
export function validateIban(raw) {
  const normalized = String(raw || "").replace(/\s+/g, "").toUpperCase();
  const base = {
    normalized,
    valid: false,
    country: null,
    countryName: null,
    expectedLength: null,
    message: "",
  };
  if (!normalized) return { ...base, valid: true, message: "" }; // campo opzionale
  if (!/^[A-Z0-9]+$/.test(normalized)) {
    return { ...base, message: "Contiene caratteri non ammessi" };
  }
  if (normalized.length < 2 || !/^[A-Z]{2}/.test(normalized)) {
    return { ...base, message: "Deve iniziare con il codice paese (2 lettere, es. IT)" };
  }
  const country = normalized.slice(0, 2);
  const spec = IBAN_SPECS[country];
  if (!spec) {
    return { ...base, country, message: `Codice paese "${country}" sconosciuto` };
  }
  const [expectedLength, countryName] = spec;
  const common = { ...base, country, countryName, expectedLength };
  if (normalized.length !== expectedLength) {
    return {
      ...common,
      message: `${countryName}: attesi ${expectedLength} caratteri, inseriti ${normalized.length}`,
    };
  }
  if (!/^\d{2}$/.test(normalized.slice(2, 4))) {
    return {
      ...common,
      message: "Le cifre di controllo (3ª e 4ª posizione) devono essere numeriche",
    };
  }
  return { ...common, valid: true, message: `IBAN valido — ${countryName} (${expectedLength} caratteri)` };
}

/** Formatta un IBAN in gruppi di 4 per la visualizzazione. */
export function formatIban(iban) {
  return String(iban || "").replace(/\s+/g, "").replace(/(.{4})/g, "$1 ").trim();
}
